import type { ReactNode } from 'react';
import { CircleAlert, LoaderCircle, RefreshCw, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './controls';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

/** Nothing here yet: say what will appear and offer the one action that fills it. */
export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  compact,
}: {
  icon?: LucideIcon;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cx('state', compact && 'state-compact')}>
      {Icon && (
        <span className="state-icon" aria-hidden>
          <Icon size={24} strokeWidth={1.75} />
        </span>
      )}
      <h2 className="state-title">{title}</h2>
      {body && <p className="state-body">{body}</p>}
      {action && <div className="state-action">{action}</div>}
    </div>
  );
}

/** Something failed: name the problem, keep the user's data safe, offer the retry. */
export function ErrorState({
  title,
  body,
  onRetry,
  compact,
}: {
  title?: string;
  body?: ReactNode;
  onRetry?: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={cx('state', 'state-error', compact && 'state-compact')} role="alert">
      <span className="state-icon" aria-hidden>
        <CircleAlert size={24} strokeWidth={1.75} />
      </span>
      <h2 className="state-title">{title ?? t('states.errorTitle')}</h2>
      <p className="state-body">{body ?? t('states.errorBody')}</p>
      {onRetry && (
        <div className="state-action">
          <Button icon={RefreshCw} onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <span className="spinner" role="status">
      <LoaderCircle aria-hidden className="spin" size={20} />
      <span className="visually-hidden">{label ?? t('common.loading')}</span>
    </span>
  );
}

/** Placeholder block in the shape of the content that is coming. */
export function Skeleton({ width = '100%', height = 16, radius }: { width?: number | string; height?: number | string; radius?: number }) {
  return <span className="skeleton" style={{ width, height, borderRadius: radius }} aria-hidden />;
}

/**
 * Loading state shaped like the list or chart it replaces, so the layout
 * does not jump when data lands.
 */
export function LoadingState({ rows = 3, variant = 'list' }: { rows?: number; variant?: 'list' | 'chart' | 'hero' }) {
  const { t } = useTranslation();
  return (
    <div className={`loading loading-${variant}`} role="status" aria-busy="true">
      <span className="visually-hidden">{t('common.loading')}</span>
      {variant === 'hero' && (
        <>
          <Skeleton width="38%" height={16} />
          <Skeleton width="62%" height={56} radius={10} />
          <Skeleton width="100%" height={10} radius={999} />
        </>
      )}
      {variant === 'chart' && (
        <div className="loading-bars">
          {[42, 64, 50, 78, 58, 86].map((h, i) => (
            <Skeleton key={i} width="100%" height={`${h}%`} radius={4} />
          ))}
        </div>
      )}
      {variant === 'list' &&
        Array.from({ length: rows }, (_, i) => (
          <div className="loading-row" key={i}>
            <Skeleton width={12} height={12} radius={999} />
            <Skeleton width={`${46 + ((i * 17) % 30)}%`} height={14} />
            <Skeleton width={56} height={14} />
          </div>
        ))}
    </div>
  );
}
